
import React from 'react';
import { Position } from '@/types/technique';
import { FilterCombobox, FilterOption } from '@/components/filters/FilterCombobox';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface FilterOptionsProps {
  positions: Position[];
  selectedPosition: string | undefined;
  selectedTechniquePosition: string | undefined;
  selectedTechniqueType: string | undefined;
  selectedDifficulty: string | undefined;
  excludeUserCreated?: boolean;
  onPositionChange: (value: string) => void;
  onTechniquePositionChange: (value: string) => void;
  onTechniqueTypeChange: (value: string) => void;
  onDifficultyChange: (value: string) => void;
  onExcludeUserCreatedChange?: (value: boolean) => void;
}

export const FilterOptions: React.FC<FilterOptionsProps> = ({
  positions,
  selectedPosition,
  selectedTechniquePosition,
  selectedTechniqueType,
  selectedDifficulty,
  excludeUserCreated = false,
  onPositionChange,
  onTechniquePositionChange,
  onTechniqueTypeChange,
  onDifficultyChange,
  onExcludeUserCreatedChange
}) => {
  const positionOptions: FilterOption[] = [
    { value: 'all', label: 'All Positions' },
    ...positions.map(position => ({ value: position.id, label: position.name }))
  ];
  
  const techniquePositionOptions: FilterOption[] = [
    { value: 'all', label: 'All Position Types' },
    { value: 'standing', label: 'Standing' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'transitional', label: 'Transitional' }
  ];
  
  const techniqueTypeOptions: FilterOption[] = [
    { value: 'all', label: 'All Technique Types' },
    { value: 'choke', label: 'Choke' },
    { value: 'joint_lock', label: 'Joint Lock' },
    { value: 'sweep', label: 'Sweep' },
    { value: 'takedown', label: 'Takedown' },
    { value: 'control', label: 'Control' },
    { value: 'escape', label: 'Escape' },
    { value: 'defensive', label: 'Defensive' },
    { value: 'guard_passing', label: 'Guard Passing' },
    { value: 'other', label: 'Other' }
  ];
  
  const difficultyOptions: FilterOption[] = [
    { value: 'all', label: 'All Difficulty Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
      <FilterCombobox 
        options={positionOptions}
        selectedValue={selectedPosition || 'all'}
        onSelect={onPositionChange}
        placeholder="Position"
        searchPlaceholder="Search position..."
        emptyMessage="No position found."
      />
      
      <FilterCombobox 
        options={techniquePositionOptions}
        selectedValue={selectedTechniquePosition || 'all'}
        onSelect={onTechniquePositionChange}
        placeholder="Position Type"
        searchPlaceholder="Search position type..."
        emptyMessage="No position type found."
      />
      
      <FilterCombobox 
        options={techniqueTypeOptions}
        selectedValue={selectedTechniqueType || 'all'}
        onSelect={onTechniqueTypeChange}
        placeholder="Technique Type"
        searchPlaceholder="Search technique type..."
        emptyMessage="No technique type found."
      />
      
      <FilterCombobox 
        options={difficultyOptions}
        selectedValue={selectedDifficulty || 'all'}
        onSelect={onDifficultyChange}
        placeholder="Difficulty Level"
        searchPlaceholder="Search difficulty level..."
        emptyMessage="No difficulty level found."
      />
      
      {onExcludeUserCreatedChange && (
        <div className="flex items-center space-x-2 col-span-1 md:col-span-2">
          <Checkbox 
            id="hide-user-created" 
            checked={excludeUserCreated}
            onCheckedChange={(checked: boolean) =>
              onExcludeUserCreatedChange(checked as boolean)
            }
          />
          <Label htmlFor="hide-user-created">Hide user-created techniques</Label>
        </div>
      )}
    </div>
  );
};
